import clsx from 'clsx';
import { get, isNil } from 'lodash-es';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Form,
  FormControlProps,
  Icon,
  IconProps,
  Text,
  Tooltip,
  useConfiguredForm,
} from '../index';
import { FormFeedback, FormGroup, Label } from './common';
import './text-field.scss';
import { FormTranslateFn } from '@wyz/types';
import * as React from 'react';
type CommonProps = Omit<FormControlProps, 'translate' | 'size'> & {
  name: string;
  label?: React.ReactNode;
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  tooltips?: React.ReactNode;
  formGroupClassName?: string;
  cols?: number;
  rows?: number;
  maxLength?: number;
  feedbackType?: 'tooltip' | 'inline';
  icon?: IconProps & { onClick?: () => void };
  size?: 'sm' | 'lg' | 'xs';
};
export type TextFieldProps = (
  | { type?: 'text' }
  | { type: 'color' }
  | { type: 'search' }
  | { type: 'password' }
  | { type: 'number'; min?: number; max?: number }
) &
  CommonProps;

type PrivateProps<TKeys extends string> = TextFieldProps & {
  translate: FormTranslateFn<TKeys>;
};

const TextField = <TKeys extends string>({
  label,
  type = 'text',
  helperText,
  tooltips,
  translate,
  formGroupClassName = 'mb-2',
  maxLength,
  feedbackType = 'inline',
  icon,
  size = 'sm',
  ...props
}: PrivateProps<TKeys>) => {
  const methods = useFormContext();
  const errorMessage = get(
    methods.formState.errors,
    `${props.name}.message`,
  ) as unknown as TKeys;
  const configurationContext = useConfiguredForm();
  const watchedValue = useWatch({ name: props.name });
  return (
    <FormGroup className={clsx(formGroupClassName, `input-${size || 'lg'}`)}>
      {label && (
        <div className='space-x-2 position-relative'>
          {tooltips && (
            <div className='position-absolute'>
              <Tooltip placement='right' label={tooltips}>
                <Icon icon={'info-circle'} width={13} height={13} />
              </Tooltip>
            </div>
          )}
          <Label
            htmlFor={props.id || props.name}
            className={clsx({
              'ms-3': tooltips,
              'required': props.required,
            })}
            style={{
              visibility: props.hidden ? 'hidden' : 'visible',
            }}
          >
            {label}
            {!isNil(maxLength) && (
              <span> {` (${watchedValue?.length || 0}/${maxLength})`}</span>
            )}
          </Label>
        </div>
      )}
      {/* workaround to add a divider with css */}
      <div id='input-container' className='position-relative'>
        {configurationContext.mode === 'WRITE' ? (
          <Form.Control
            {...props}
            size={size as 'sm' | 'lg'} // react-boostrap typing only supports sm and lg although we can technically have xs too
            className={clsx(props.className, {
              'empty-color': !methods.getValues(props.name),
            })}
            style={{
              paddingRight: isNil(icon)
                ? undefined
                : errorMessage
                  ? '3.5rem'
                  : '1.8rem',
            }}
            id={props.id || props.name}
            type={type}
            isInvalid={!!errorMessage}
            {...methods.register(props.name, {
              setValueAs: (value) => {
                if (type === 'number') {
                  const maybeNumber =
                    typeof value === 'string' ? parseFloat(value) : value;
                  if (
                    Number.isFinite(maybeNumber) &&
                    !Number.isNaN(maybeNumber)
                  ) {
                    return maybeNumber;
                  }
                  return null;
                }
                return value;
              },
            })}
            data-cy={props.name}
            data-testid={props.name}
          />
        ) : (
          <Text heading='normal' weight='semi-bold'>
            {type === 'color' ? (
              <>
                {methods.getValues(props.name) ? (
                  <span
                    className='d-block'
                    style={{
                      height: 10,
                      width: 50,
                      background: methods.getValues(props.name),
                      border: '1px solid',
                    }}
                  ></span>
                ) : (
                  '-'
                )}
              </>
            ) : (
              <>
                <input type={type} {...methods.register(props.name)} hidden />
                {methods.getValues(props.name) || '-'}
              </>
            )}
          </Text>
        )}
        {!isNil(icon) && (
          <Icon
            className={clsx('position-absolute', {
              'cursor-pointer': !isNil(icon.onClick),
            })}
            style={{
              right: errorMessage ? 35 : 5,
              top: size === 'lg' ? 11 : 6,
            }}
            {...icon}
          />
        )}
      </div>
      {errorMessage ? (
        <FormFeedback invalid tooltip={feedbackType === 'tooltip'}>
          {translate(errorMessage, { length: maxLength })}
        </FormFeedback>
      ) : helperText ? (
        <FormFeedback>{helperText}</FormFeedback>
      ) : null}
    </FormGroup>
  );
};

export default TextField;
